// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  Outlet,
} from "react-router";
import Login from "routes/login.js";
import Dashboard from "routes/dashboard.js";
import Layout from "./components/layout.js";
import "./utils/i18n";
import Register from "routes/register.js";
import RegisterAdmin from "routes/register-admin.js";
import RegisterAdminSuccess from "routes/register-admin-success.js";
import CompleteAdminProfile from "routes/complete-admin-profile.js";
import FirstStep from "routes/first-step.js";
import Invitation from "components/register/invitation.js";
import membershipData from "hoc/membership-data.js";
import { PrimeReactProvider } from "primereact/api";
import TeamPreferences from "routes/teams/team-preferences.js";
import TeamIndex from "routes/teams/index.js";
import TeamManagement from "routes/teams/team-management.js";
import InvitationIndex from "routes/invitations/index.js";
import RegisterPlayer from "routes/register-player.js";
import Params from "routes/parameters/index.js";
import tokenService from "services/token.service";
import NotFound from "components/functional/not-found.js";
import ForgotPassword from "routes/password/forgot-password.js";
import ResetPassword from "routes/password/reset-password.js";
import ChangePasswordSuccess from "routes/password/change-password-success.js";
import PermissionGuard from "guards/permissions.guard.js";
import Reports from "routes/reports.js";
import ErrorBoundary from "components/functional/error-boundary.js";

const MembershipData = membershipData(() => <Outlet />);

const ProtectedRoute = () => {
  const isTokenValid = tokenService.isValid();

  if (!isTokenValid) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

const App = () => {
  return (
    <ErrorBoundary>
      <PrimeReactProvider>
        <Router
          future={{
            v7_relativeSplatPath: true,
            v7_startTransition: true,
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate replace to="/login" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-admin" element={<RegisterAdmin />} />
            <Route path="/register-player" element={<RegisterPlayer />} />
            <Route
              path="/invitations/:invitation_id"
              element={<Invitation />}
            />
            <Route path="/password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route
              path="/reset-password-success"
              element={<ChangePasswordSuccess />}
            />
            <Route element={<ProtectedRoute />}>
              <Route
                path="/register-admin-success"
                element={<RegisterAdminSuccess />}
              />
              <Route path="/first-step" element={<FirstStep />} />
              <Route element={<MembershipData />}>
                <Route
                  path="/complete-admin-profile"
                  element={<CompleteAdminProfile />}
                />
                <Route path="/" element={<Layout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/recommend" element={<Dashboard />} />
                  <Route path="/teams" element={<TeamIndex />} />
                  <Route
                    element={
                      <PermissionGuard requiredPermission="manage_preferences" />
                    }
                  >
                    <Route
                      path="/teams/:team_id/preferences"
                      element={<TeamPreferences />}
                    />
                  </Route>
                  <Route
                    element={
                      <PermissionGuard requiredPermission="manage_team" />
                    }
                  >
                    <Route
                      path="/teams/:team_id/management"
                      element={<TeamManagement />}
                    />
                  </Route>
                  <Route
                    path="/teams/:team_id/invite"
                    element={<InvitationIndex />}
                  />
                  <Route path="/parameters" element={<Params />} />
                  <Route path="/reports" element={<Reports />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </PrimeReactProvider>
    </ErrorBoundary>
  );
};

export default App;
